<script setup lang="ts">
import type { MenuItem , ParentMenuItem } from "@/layout/main/menu";

import { useRoute } from "vue-router";

import { useLayout } from "@/layout/common/composables/layout";
import menuItems, { hasItems, isExternal } from "@/layout/main/menu";
import Sidebar from "@/layout/main/sidebar";
import { LONG_MENU_LABELS } from "@/service/mockValuesService";
import { ThemeToggle } from "@/components/ui";

import Logo from "@/components/icons/Logo.vue";

const route = useRoute();

const { calculatedMenuMode } = useLayout();

// Type assertion: we know all menuItems have items
const typedMenuItems = menuItems as ParentMenuItem[];

// Function to check if a menu item is active
const isMenuItemActive = (item: MenuItem): boolean => {
  const itemTo = "to" in item ? item.to : undefined;

  if (!itemTo) {
    // Parent items without direct routes are not highlighted
    return false;
  }

  // Highlight Participants base items for all their sub-routes (list, create, detail, edit)
  if (itemTo.startsWith("/backoffice/participants/")) {
    if (route.path.startsWith(itemTo)) {
      return true;
    }
  }

  // Highlight Aid Accounts submenu for any savings account routes (supports legacy /aids and current /aid paths)
  if (
    itemTo === "/backoffice/aid/chart-of-accounts" ||
    itemTo === "/backoffice/aids/chart-of-accounts"
  ) {
    if (
      route.path.startsWith("/backoffice/aid/savings-accounts") ||
      route.path.startsWith("/backoffice/aids/savings-accounts")
    ) {
      return true;
    }
  }

  // Highlight Groups for list, create, edit, and detail routes
  if (itemTo === "/backoffice/groups") {
    if (
      route.path === "/backoffice/groups" ||
      route.path === "/backoffice/groups/create" ||
      route.path.startsWith("/backoffice/groups/")
    ) {
      return true;
    }
  }

  // Highlight Products for list, create, and edit (detail) routes
  if (itemTo === "/backoffice/bank/bank-products-list") {
    if (
      route.path === "/backoffice/bank/bank-products-list" ||
      route.path.startsWith("/backoffice/bank/bank-products-create") ||
      route.path.startsWith("/backoffice/bank/bank-product/")
    ) {
      return true;
    }
  }

  // Highlight Manage Locations for list, create, edit, detail, and import routes
  if (itemTo === "/backoffice/organization/manage-locations") {
    if (
      route.path === "/backoffice/organization/manage-locations" ||
      route.path === "/backoffice/offices/create" ||
      route.path.startsWith("/backoffice/offices/") ||
      route.path === "/backoffice/import-offices"
    ) {
      return true;
    }
  }

  // Highlight Manage Employees for list, create, and detail routes
  if (itemTo === "/backoffice/organization/manage-employees") {
    if (
      route.path === "/backoffice/organization/manage-employees" ||
      route.path === "/backoffice/employee/create" ||
      route.path.startsWith("/backoffice/employee/")
    ) {
      return true;
    }
  }

  // For group detail pages, check if the current route matches the group ID
  if (itemTo.includes("/groups/") && !itemTo.includes("/create")) {
    const groupId = itemTo.match(/\/groups\/(\d+)/)?.[1];

    if (groupId) {
      return route.path.includes(`/groups/${groupId}`);
    }
  }

  // Generic rule: active on exact path or any nested sub-route
  if (
    itemTo &&
    (route.path === itemTo || route.path.startsWith(itemTo + "/"))
  ) {
    return true;
  }

  return false;
};

// Function to get shortened label and tooltip for menu items
const getMenuLabel = (item: MenuItem) => {
  const labelKey = item.label as keyof typeof LONG_MENU_LABELS;

  if (LONG_MENU_LABELS[labelKey]) {
    return {
      label: LONG_MENU_LABELS[labelKey].short,
      tooltip: LONG_MENU_LABELS[labelKey].tooltip,
    };
  }

  const tooltip = "tooltip" in item ? item.tooltip : undefined;

  return {
    label: item.label,
    tooltip: tooltip || item.label,
  };
};
</script>

<template>
  <Sidebar>
    <!-- Header -->
    <Sidebar.Header>
      <RouterLink to="/" class="flex items-center gap-2">
        <div class="flex items-center justify-center">
          <Logo class="h-full w-auto" />
        </div>
      </RouterLink>
    </Sidebar.Header>
    <!-- Content -->
    <Sidebar.Content
      v-if="
        calculatedMenuMode !== 'slim' &&
        calculatedMenuMode !== 'compact' &&
        calculatedMenuMode !== 'horizontal'
      "
    >
      <Sidebar.Group v-for="menuItem in typedMenuItems" :key="menuItem.label">
        <Sidebar.GroupLabel>{{ menuItem.label }}</Sidebar.GroupLabel>
        
        <Sidebar.GroupContent>
          <Sidebar.Menu>
            <Sidebar.MenuItem
              v-for="item in menuItem.items"
              :key="item.label"
              :collapsible="hasItems(item)"
            >
              <Sidebar.MenuButton
                v-if="isExternal(item)"
                :href="item.url"
                :target="item.target"
                :is-active="isMenuItemActive(item)"
                :is-first="true"
              >
                <i :class="item.icon" class="!leading-none !text-sm" />
                <span>{{ item.label }}</span>
              </Sidebar.MenuButton>
              <Sidebar.MenuButton
                v-else
                :to="item.to"
                :is-active="isMenuItemActive(item)"
                :is-first="true"
              >
                <i :class="item.icon" class="!leading-none !text-sm" />
                <span>{{ item.label }}</span>
              </Sidebar.MenuButton>
              <Sidebar.Menu v-if="hasItems(item)" class="submenu-items">
                <Sidebar.MenuItem
                  v-for="subItem in item.items"
                  :key="subItem.label"
                >
                  <Sidebar.MenuButton
                    v-if="isExternal(subItem)"
                    :href="subItem.url"
                    :target="subItem.target"
                    :is-active="isMenuItemActive(subItem)"
                    :tooltip-value="getMenuLabel(subItem).tooltip"
                    class="submenu-button"
                  >
                    <i
                      :class="subItem.icon"
                      class="!leading-none !text-sm flex-shrink-0"
                    />
                    <span class="truncate max-w-[140px]">{{
                      getMenuLabel(subItem).label
                    }}</span>
                  </Sidebar.MenuButton>
                  <Sidebar.MenuButton
                    v-else
                    :to="subItem.to"
                    :is-active="isMenuItemActive(subItem)"
                    :tooltip-value="getMenuLabel(subItem).tooltip"
                    class="submenu-button"
                  >
                    <i
                      :class="subItem.icon"
                      class="!leading-none !text-sm flex-shrink-0"
                    />
                    <span class="truncate max-w-[140px]">{{
                      getMenuLabel(subItem).label
                    }}</span>
                  </Sidebar.MenuButton>
                </Sidebar.MenuItem>
              </Sidebar.Menu>
            </Sidebar.MenuItem>
          </Sidebar.Menu>
        </Sidebar.GroupContent>
      </Sidebar.Group>
      <!-- Footer with Theme Toggle -->
      <Sidebar.Footer>
        <ThemeToggle />
      </Sidebar.Footer>
    </Sidebar.Content>

    <Sidebar.Content v-else>
      <Sidebar.Group>
        <Sidebar.GroupContent>
          <Sidebar.Menu>
            <Sidebar.MenuItem v-for="item in menuItems" :key="item.label">
              <Sidebar.Popover
                :placement="
                  calculatedMenuMode === 'horizontal' ? 'bottom' : 'right'
                "
              >
                <template #trigger>
                  <Sidebar.MenuButton
                    v-if="isExternal(item)"
                    :href="item.url"
                    :target="item.target"
                    :isActive="isMenuItemActive(item)"
                    :tooltip-value="
                      calculatedMenuMode === 'compact' ? item.label : ''
                    "
                  >
                    <i :class="item.icon" class="!leading-none !text-sm" />
                    <span>{{ item.label }}</span>
                  </Sidebar.MenuButton>
                  <Sidebar.MenuButton
                    v-else
                    :to="item.to"
                    :isActive="isMenuItemActive(item)"
                    :tooltip-value="
                      calculatedMenuMode === 'compact' ? item.label : ''
                    "
                  >
                    <i :class="item.icon" class="!leading-none !text-sm" />
                    <span>{{ item.label }}</span>
                  </Sidebar.MenuButton>
                </template>
                <template #content v-if="hasItems(item)">
                  <Sidebar.Menu mode="static">
                    <Sidebar.MenuItem
                      v-for="subItem in item.items"
                      :key="subItem.label"
                      :collapsible="hasItems(subItem)"
                    >
                      <Sidebar.MenuButton
                        v-if="isExternal(subItem)"
                        mode="static"
                        :href="subItem.url"
                        :target="subItem.target"
                        :isActive="isMenuItemActive(subItem)"
                        :tooltip-value="getMenuLabel(subItem).tooltip"
                        class="submenu-button"
                      >
                        <i
                          :class="subItem.icon"
                          class="!leading-none !text-sm flex-shrink-0"
                        />
                        <span class="truncate max-w-[140px]">{{
                          getMenuLabel(subItem).label
                        }}</span>
                      </Sidebar.MenuButton>
                      <Sidebar.MenuButton
                        v-else
                        mode="static"
                        :to="subItem.to"
                        :isActive="isMenuItemActive(subItem)"
                        :tooltip-value="getMenuLabel(subItem).tooltip"
                        class="submenu-button"
                      >
                        <i
                          :class="subItem.icon"
                          class="!leading-none !text-sm flex-shrink-0"
                        />
                        <span class="truncate max-w-[140px]">{{
                          getMenuLabel(subItem).label
                        }}</span>
                      </Sidebar.MenuButton>
                      <Sidebar.Menu mode="static" v-if="hasItems(subItem)">
                        <Sidebar.MenuItem
                          v-for="subSubItem in subItem.items"
                          :key="subSubItem.label"
                        >
                          <Sidebar.MenuButton
                            v-if="isExternal(subSubItem)"
                            mode="static"
                            :href="subSubItem.url"
                            :target="subSubItem.target"
                            :isActive="isMenuItemActive(subSubItem)"
                            :tooltip-value="getMenuLabel(subSubItem).tooltip"
                            class="submenu-button"
                          >
                            <i
                              :class="subSubItem.icon"
                              class="!leading-none !text-sm flex-shrink-0"
                            />
                            <span class="truncate max-w-[140px]">{{
                              getMenuLabel(subSubItem).label
                            }}</span>
                          </Sidebar.MenuButton>
                          <Sidebar.MenuButton
                            v-else
                            mode="static"
                            :to="subSubItem.to"
                            :isActive="isMenuItemActive(subSubItem)"
                            :tooltip-value="getMenuLabel(subSubItem).tooltip"
                            class="submenu-button"
                          >
                            <i
                              :class="subSubItem.icon"
                              class="!leading-none !text-sm flex-shrink-0"
                            />
                            <span class="truncate max-w-[140px]">{{
                              getMenuLabel(subSubItem).label
                            }}</span>
                          </Sidebar.MenuButton>
                        </Sidebar.MenuItem>
                      </Sidebar.Menu>
                    </Sidebar.MenuItem>
                  </Sidebar.Menu>
                </template>
              </Sidebar.Popover>
            </Sidebar.MenuItem>
          </Sidebar.Menu>
        </Sidebar.GroupContent>
      </Sidebar.Group>
      <!-- Footer with Theme Toggle -->
      <Sidebar.Footer v-if="calculatedMenuMode !== 'horizontal'">
        <ThemeToggle />
      </Sidebar.Footer>
    </Sidebar.Content>
  </Sidebar>
</template>

<style scoped>
.submenu-items {
  /* Ensure proper spacing for nested items */
  padding-left: 1.5rem;
}

.submenu-button {
  /* Ensure proper text truncation for submenu items */
  min-width: 0;
  overflow: hidden;
}

.submenu-button span {
  /* Force text truncation with ellipsis */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 140px;
  display: block;
  min-width: 0;
}

/* Ensure icons don't shrink */
.submenu-button i {
  flex-shrink: 0;
  min-width: 1rem;
}
</style>
